import "./css/App.css"
import "./css/breaking-news-ticker.min.css"

import VicinityContext from "./context/vicinityContext"

import { Outlet, useLocation } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import React, { useEffect, useRef, useState } from "react"
import Vicinitizer from "./components/Vicinitizer"
import ScrollToTop from "./components/ScrollToTop"
import Header from "./components/Header"
import Footer from "./components/Footer"
import WelcomeModal from "./components/shared/WelcomeModal"
import NotificationCenterModal from "./components/NotificationCenterModal"
import { useNotifications } from "./hooks/useNotifications"
import { useGeolocationPermission } from "./hooks/useGeolocationPermission"
import { syncUserLocation } from "./utilities/locationSync"
import { consoleLog } from "./utilities/helpers"
import {
    LOCALSTORAGE_LOCATIONPERMISSION,
    LOCALSTORAGE_PLACE,
    LOCALSTORAGE_COUNTRYCODE,
    SESSIONSTORAGE_LOCATION,
} from "./const/storage"
import { usePreferences } from "./hooks/usePreferences"
import { useAuth } from "./hooks/useAuth"
import { isNoIndexPath } from "./utilities/seo"

function App() {
    const routeLocation = useLocation()
    const isLoggedIn = useAuth()
    // Logged-out visitors on an Analysis page see SharePreviewCard, which
    // needs no location. Until they log in or go elsewhere: no welcome modal
    // covering the preview (the "first visit" state is kept), and no location
    // requests at all, even with Chaperon's setting on "allow".
    const isLoggedOutPreview =
        !isLoggedIn && routeLocation.pathname.startsWith("/analysis/")
    // The interval's pollUserLocation keeps its first render's closure, so
    // it reads this ref instead.
    const isLoggedOutPreviewRef = useRef(isLoggedOutPreview)
    isLoggedOutPreviewRef.current = isLoggedOutPreview
    const hasRequestedLocationRef = useRef(false)

    const [location, setLocation] = useState({
        coordinates: { latitude: 0, longitude: 0 },
        timestamp: null,
    })
    const [countryCode, setCountryCode] = useState("GLOBAL")
    const [place, setPlace] = useState({
        imageUrl:
            "https://storage.googleapis.com/chaperon/uploads/world-map.png",
        fromGoogle: {
            photo_cover:
                "https://storage.googleapis.com/chaperon/uploads/world-map.png",
        },
    })
    const [spotlightPlaces, setSpotlightPlaces] = useState([])
    const [news, setNews] = useState([])
    const [showToast, setShowToast] = useState(false)
    const [toastMessage, setToastMessage] = useState("Loading data...")
    const [toastOptions, setToastOptions] = useState({ icon: "grow" })
    const [showWelcomeModal, setShowWelcomeModal] = useState(false)
    const [userId, setUserId] = useState(null)
    const [showNotificationModal, setShowNotificationModal] = useState(false)

    // Get notifications from API
    const { notifications, fetchNotifications } = useNotifications(userId)
    const { locationRefreshMinutes } = usePreferences()

    // Chaperon's own location setting (allow/deny/ask) and the browser's
    // permission; "Detect my current location" needs both.
    const [locationSetting, setLocationSetting] = useState(() =>
        localStorage.getItem(LOCALSTORAGE_LOCATIONPERMISSION),
    )
    const browserGeolocation = useGeolocationPermission()
    const canDetectLocation =
        Boolean(navigator.geolocation) &&
        locationSetting === "allow" &&
        browserGeolocation !== "denied"

    // Initialize user ID from localStorage
    useEffect(() => {
        const userData = localStorage.getItem("chaperon_user_data")
        if (userData) {
            const user = JSON.parse(userData)
            setUserId(user.id)
        }
    }, [])

    useEffect(() => {
        const handleNotificationCreated = () => {
            fetchNotifications()
        }

        window.addEventListener(
            "notificationCreated",
            handleNotificationCreated,
        )

        return () => {
            window.removeEventListener(
                "notificationCreated",
                handleNotificationCreated,
            )
        }
    }, [fetchNotifications])

    // Global Bootstrap tooltip initialization with MutationObserver
    useEffect(() => {
        // Check if Bootstrap is available
        if (!window.bootstrap?.Tooltip) {
            return
        }

        // Initialize tooltips only for elements that don't have them yet
        const initializeNewTooltips = () => {
            const tooltipTriggerList = document.querySelectorAll(
                '[data-bs-toggle="tooltip"]',
            )
            tooltipTriggerList.forEach((tooltipTriggerEl) => {
                try {
                    // Only create tooltip if it doesn't already exist
                    if (
                        !window.bootstrap.Tooltip.getInstance(tooltipTriggerEl)
                    ) {
                        new window.bootstrap.Tooltip(tooltipTriggerEl, {
                            delay: { show: 750, hide: 0 },
                        })
                    }
                } catch (e) {
                    // Silently handle errors - element may not be valid for tooltip
                }
            })
        }

        // Initialize tooltips on component mount
        initializeNewTooltips()

        // Set up a MutationObserver to watch for dynamically added elements
        const observer = new MutationObserver(() => {
            // Debounce tooltip initialization to avoid excessive processing
            clearTimeout(observer.debounceTimer)
            observer.debounceTimer = setTimeout(initializeNewTooltips, 100)
        })

        // Observe body for changes (added/removed elements)
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        })

        // Handle the reinitializeTooltips event for manual triggers
        const handleReinitializeTooltips = () => {
            initializeNewTooltips()
        }

        window.addEventListener(
            "reinitializeTooltips",
            handleReinitializeTooltips,
        )

        // Cleanup
        return () => {
            clearTimeout(observer.debounceTimer)
            observer.disconnect()
            window.removeEventListener(
                "reinitializeTooltips",
                handleReinitializeTooltips,
            )
            // Dispose all tooltips on component unmount
            try {
                const tooltips = document.querySelectorAll(
                    '[data-bs-toggle="tooltip"]',
                )
                tooltips.forEach((el) => {
                    try {
                        const tooltip = window.bootstrap.Tooltip.getInstance(el)
                        if (tooltip) {
                            tooltip.dispose()
                        }
                    } catch (e) {
                        // Silently handle errors
                    }
                })
            } catch (e) {
                // Silently handle errors
            }
        }
    }, [routeLocation])

    const getLocation = () => {
        const x = document.getElementById("location-status")

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(showPosition)
        } else if (x) {
            x.innerHTML = "Geolocation is not supported by this browser."
        }
    }

    // Background refresh for users whose only location source is this web
    // app. Deliberately bypasses showPosition so the shared location/place
    // state (and the map) isn't disturbed by a timer. Never prompts: it only
    // runs when Chaperon's setting is "allow" and the browser has already
    // granted geolocation. The device-token and other-writer checks live in
    // syncUserLocation.
    const pollUserLocation = async () => {
        if (
            isLoggedOutPreviewRef.current ||
            localStorage.getItem(LOCALSTORAGE_LOCATIONPERMISSION) !== "allow" ||
            !navigator.geolocation
        ) {
            return
        }

        if (navigator.permissions?.query) {
            try {
                const status = await navigator.permissions.query({
                    name: "geolocation",
                })
                if (status.state !== "granted") return
            } catch {
                // Permissions API unavailable for geolocation; rely on the
                // Chaperon setting alone.
            }
        }

        navigator.geolocation.getCurrentPosition(
            (position) => syncUserLocation(position.coords),
            (error) => consoleLog("Location poll failed:", error.message),
        )
    }

    const showPosition = (position) => {
        const x = document.getElementById("location-status")
        if (x) {
            x.innerHTML = `<i class="bi bi-geo-alt"></i> `
            x.innerHTML +=
                "Latitude: " +
                position.coords.latitude +
                " | Longitude: " +
                position.coords.longitude
        }

        const locationData = {
            coordinates: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            },
            timestamp: position.timestamp,
        }

        setLocation(locationData)

        sessionStorage.setItem(
            SESSIONSTORAGE_LOCATION,
            JSON.stringify(locationData),
        )

        syncUserLocation(locationData.coordinates)
    }

    const handleLocationPermission = (permission) => {
        // Mark that user has seen the welcome modal
        localStorage.setItem(LOCALSTORAGE_LOCATIONPERMISSION, permission)
        setLocationSetting(permission)

        if (permission === "allow" || permission === "deny") {
            setShowWelcomeModal(false)
        }

        if (permission === "ask") {
            setShowWelcomeModal(true)
        }

        if (permission === "allow") {
            getLocation()
        }
    }

    useEffect(() => {
        // Check Chaperon location permission setting
        const locationPermissionSetting = localStorage.getItem(
            LOCALSTORAGE_LOCATIONPERMISSION,
        )

        if (
            locationPermissionSetting === null ||
            locationPermissionSetting === "ask"
        ) {
            // Show welcome modal for first-time visitors or if they chose to be asked again
            setShowWelcomeModal(true)
        }

        if (
            locationPermissionSetting === "allow" &&
            !isLoggedOutPreview &&
            !hasRequestedLocationRef.current
        ) {
            // For returning visitor with the allow setting, proceed with
            // location request -- once, and not on a logged-out preview
            // (it runs when they leave it or log in).
            hasRequestedLocationRef.current = true
            getLocation()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedOutPreview])

    // Called from Settings. Records the choice without reopening the welcome
    // modal; "allow" starts sharing now (and is where the browser's own
    // permission prompt appears, if it has not been answered yet).
    const setLocationPermission = (permission) => {
        localStorage.setItem(LOCALSTORAGE_LOCATIONPERMISSION, permission)
        setLocationSetting(permission)

        if (permission === "allow") {
            getLocation()
        }
    }

    useEffect(() => {
        const intervalId = setInterval(
            pollUserLocation,
            locationRefreshMinutes * 60 * 1000,
        )
        return () => clearInterval(intervalId)
        // pollUserLocation only reads localStorage/browser APIs, so the
        // first render's closure is always current.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationRefreshMinutes])

    // Sync place data to localStorage whenever it changes
    useEffect(() => {
        if (place && place.id) {
            localStorage.setItem(LOCALSTORAGE_PLACE, JSON.stringify(place))
        }
    }, [place])

    // Sync countryCode to localStorage whenever it changes
    useEffect(() => {
        if (countryCode && countryCode !== "GLOBAL") {
            localStorage.setItem(
                LOCALSTORAGE_COUNTRYCODE,
                JSON.stringify(countryCode),
            )
        }
    }, [countryCode])

    return (
        <div className="App">
            <VicinityContext.Provider
                value={{
                    location,
                    countryCode,
                    place,
                    news,
                    spotlightPlaces,
                    showToast,
                    toastMessage,
                    toastOptions,
                    showNotificationModal,
                    notifications,
                    userId,
                    canDetectLocation,
                    fetchNotifications,
                    setLocation,
                    setCountryCode,
                    setPlace,
                    setNews,
                    setSpotlightPlaces,
                    setShowToast,
                    setToastMessage,
                    setToastOptions,
                    setShowNotificationModal,
                    setLocationPermission,
                }}
            >
                {isNoIndexPath(routeLocation.pathname) && (
                    <Helmet>
                        <meta name="robots" content="noindex" />
                    </Helmet>
                )}
                <Vicinitizer />
                <ScrollToTop />
                <Header
                    notificationsCount={
                        notifications.filter(
                            (n) =>
                                (n.status === "pending" ||
                                    n.status === "unread") &&
                                n.channel === "in_app",
                        ).length
                    }
                    onShowNotifications={() => setShowNotificationModal(true)}
                />

                {showWelcomeModal && !isLoggedOutPreview && (
                    <WelcomeModal
                        onAllow={() => handleLocationPermission("allow")}
                        onAsk={() => handleLocationPermission("ask")}
                        onDeny={() => handleLocationPermission("deny")}
                    />
                )}

                <NotificationCenterModal
                    notifications={notifications}
                    isOpen={showNotificationModal}
                    onClose={() => setShowNotificationModal(false)}
                    userId={userId}
                    onNotificationsUpdated={fetchNotifications}
                />

                {/*<BreadCrumbs />*/}

                <Outlet />

                <Footer />
            </VicinityContext.Provider>
        </div>
    )
}

export default App
